'use client';

import { Children, forwardRef, type ButtonHTMLAttributes } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils/cn';
import { Spinner } from './spinner';

export type ButtonTheme = 'neutral' | 'accent' | 'private' | 'danger';

const themeClasses: Record<ButtonTheme, string> = {
	neutral: 'theme-neutral',
	accent: 'theme-accent',
	private: 'theme-private',
	danger: 'theme-danger',
};

const buttonVariants = cva(
	'inline-flex shrink-0 items-center justify-center font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-50 hover:duration-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer border-none select-none [-webkit-touch-callout:none] [&>svg]:shrink-0',
	{
		variants: {
			variant: {
				// Theme-aware variants (Phase 1+). Pair with `theme` prop.
				'solid-strong-theme':
					'bg-map-theme text-map-theme-contrast hover:bg-map-theme-strong/90 backdrop-blur-[12px] shadow-floating border-solid border border-transparent',
				'solid-muted-theme':
					'bg-map-theme-medium text-map-theme hover:bg-map-theme-medium/80 backdrop-blur-[12px] shadow-floating border-solid border border-transparent',
				'ghost-theme':
					'bg-transparent text-map-theme hover:bg-map-theme-weak border-solid border border-transparent',
				'outline-dashed':
					'border border-dashed border-border-muted bg-transparent text-neutral hover:bg-neutral/8',
				// Existing variants (color-bound). Kept for back-compat; migrate to theme API in Phase 2.
				'solid-strong': 'bg-neutral text-background hover:bg-neutral/90 backdrop-blur-[12px] shadow-floating border-solid border border-transparent',
				solid: 'bg-neutral/10 border-solid border border-border-muted text-neutral hover:bg-neutral/15 backdrop-blur-[12px] shadow-floating',
				'solid-muted': 'bg-neutral/8 text-neutral hover:bg-neutral/15 hover:text-neutral backdrop-blur-[12px] shadow-floating border-solid border border-transparent',
				outline: 'border-solid border border-neutral/30 bg-transparent text-neutral hover:bg-neutral/5',
				'outline-muted': 'border-solid border border-border-muted bg-transparent text-neutral-muted hover:bg-neutral/5 hover:text-neutral',
				'outline-weak': 'border-solid border border-border-weak bg-transparent text-neutral-muted hover:bg-neutral/5 hover:text-neutral',
				// Ghost carries the Linear "glass" behaviour app-wide: transparent at
				// rest, neutral/10 on hover/focus (pressScale controls the press animation).
				// For chrome that sits ON media (video player, hero film) wrap the
				// group in `.frosted-ghosts` - see styles/frosted-ghosts.css - and the hover fill
				// gains an 8px backdrop blur.
				ghost: 'bg-transparent text-neutral hover:bg-neutral/10 focus-visible:bg-neutral/10 border-solid border border-transparent',
				'ghost-black': 'bg-transparent text-black/70 hover:bg-black/10 hover:text-black border-solid border border-transparent',
				'ghost-muted': 'bg-transparent text-neutral-muted hover:bg-neutral/5 hover:text-neutral border-solid border border-transparent',
				/** @deprecated — use theme='danger' + ghost-theme once Phase 2 lands */
				danger: 'bg-transparent text-danger/60 hover:bg-danger/10 hover:text-danger border-solid border border-transparent',
				/** @deprecated — use theme='accent' + ghost-theme once Phase 2 lands */
				'ghost-accent': 'bg-transparent text-accent hover:bg-accent/10 border-solid border border-transparent',
				/** @deprecated — use theme='accent' + solid-strong-theme once Phase 2 lands */
				'solid-accent': 'bg-accent text-white hover:bg-accent/90 shadow-floating border-solid border border-transparent',
				/** @deprecated — use theme='danger' + solid-strong-theme once Phase 2 lands */
				'solid-danger': 'bg-danger text-background hover:bg-danger/90 shadow-floating border-solid border border-transparent',
				'solid-danger-muted': 'bg-danger/10 text-neutral hover:bg-danger/15 backdrop-blur-[12px] shadow-floating border-solid border border-transparent',
				/** @deprecated — use theme='success' + solid-strong-theme once Phase 2 lands */
				'solid-success': 'bg-success text-background hover:bg-success/90 shadow-floating border-solid border border-transparent',
				'solid-surface': 'bg-surface text-neutral hover:bg-surface-strong shadow-floating border-solid border border-transparent',
				'solid-surface-strong': 'bg-surface-strong text-neutral hover:bg-surface-strong shadow-floating border-solid border border-transparent',
				surface: 'bg-surface border-solid border border-border-muted text-neutral hover:bg-surface-hover',
				/** @deprecated — use theme='warning' + solid-muted-theme once Phase 2 lands */
				'solid-warning': 'bg-warning/15 text-warning font-medium hover:bg-warning/25 shadow-floating border-solid border border-transparent',
				/** @deprecated — use theme='warning' + outline once Phase 2 lands */
				'outline-warning': 'border-solid border border-warning/30 bg-transparent text-warning hover:bg-warning/10',
				/** @deprecated — use theme='danger' + outline once Phase 2 lands */
				'outline-danger': 'border-solid border border-danger/30 bg-transparent text-danger hover:bg-danger/10',
				text: 'bg-transparent text-neutral-muted hover:text-neutral [&>svg]:text-neutral-muted hover:[&>svg]:text-neutral',
				link: 'bg-transparent text-neutral underline-offset-4 hover:underline',
				'header-trigger': 'bg-transparent text-neutral/60 font-bold hover:bg-neutral/5 hover:text-neutral/80'
			},
			size: {
				xs: 'h-6 gap-1 px-2 text-[10px] rounded-[8px] [&>svg]:size-3',
				sm: 'h-8 gap-1.5 px-3 text-xs font-semibold rounded-[12px] [&>svg]:size-3.5',
				md: 'h-10 gap-2 px-4 text-sm font-semibold rounded-[16px] [&>svg]:size-[18px]',
				lg: 'h-12 gap-2 px-6 text-base rounded-[20px] [&>svg]:size-5',
				// 48px square with the sm radius kept — nests concentrically inside a
				// 16px-radius shell 8px away (welcome hero mic seat).
				fab: 'h-12 gap-2 px-4 text-sm font-semibold rounded-[12px] [&>svg]:size-5',
				pill: 'h-10 px-3 rounded-[16px] text-sm [&>svg]:size-[18px]',
				card: 'px-4 py-3 gap-3 rounded-[16px] text-sm [&>svg]:size-5',
				// Fixed-height single-line rows: `whitespace-nowrap` is not cosmetic -
				// a wrapped label in an `h-7` box overflows it. Multi-line row sizes
				// (list-lg / list-flat / list-inset, all `h-auto`) deliberately wrap.
				'list-sm': 'h-7 gap-2 px-2 text-[13px] text-left rounded-[6px] w-full justify-start whitespace-nowrap [&>svg]:size-4',
				'list-md': 'h-10 gap-2 px-4 text-sm text-left rounded-[8px] w-full justify-start whitespace-nowrap [&>svg]:size-[18px]',
				'list-lg': 'h-auto gap-2.5 px-3 py-2 text-[13px] text-left rounded-[8px] w-full justify-start items-start [&>svg]:size-4 [&>svg]:mt-0.5 [&>svg]:text-neutral-muted',
				// Touch-sized list row (>=44px) — used for PopoverItem in mobile drawer mode.
				'list-touch': 'min-h-[44px] gap-2 px-4 text-sm text-left rounded-[8px] w-full justify-start whitespace-nowrap [&>svg]:size-[18px]',
				// Touch row for side-nav drawers (marketing + docs SideNavDrawer). Like
				// list-touch but with a button radius (~0.4×height) instead of the squarer
				// list radius, so filled CTAs in the drawer read as buttons, not list rows.
				'nav-drawer': 'min-h-[44px] gap-2 px-4 text-sm text-left rounded-[16px] w-full justify-start [&>svg]:size-[18px]',
				'list-flat': 'h-auto gap-3 px-4 py-2.5 rounded-none w-full justify-start text-sm text-left [&>svg]:size-[18px]',
				// Quiet full-width link row under a panel header ("2 scheduled items ->"):
				// regular weight, hairline bottom border, small trailing glyph.
				'list-link': 'h-auto gap-3 px-4 py-2.5 rounded-none w-full justify-start text-[13px] font-normal text-left border-b border-neutral/6 whitespace-nowrap [&>svg]:size-3.5',
				// list-flat inside an inset (padded) container - rounded so the
				// hover/selected fill doesn't read as an edge-to-edge bar.
				'list-inset': 'h-auto gap-3 px-4 py-2.5 rounded-[12px] w-full justify-start text-sm text-left [&>svg]:size-[18px]',
				nav: 'h-12 gap-2 px-4 text-sm rounded-2xl [&>svg]:size-5',
				'nav-sm': 'h-9 gap-2 px-3 text-sm rounded-xl [&>svg]:size-4',
				field: 'h-auto gap-2 px-4 py-3 rounded-xl w-full justify-start text-sm [&>svg]:size-[18px]',
				badge: 'h-auto gap-1 px-2.5 py-1 text-[12px] rounded-full [&>svg]:size-3.5',
				tab: 'h-9 gap-2 px-3 text-[15px] font-semibold rounded-full [&>svg]:size-[14px]',
				'tab-sm': 'h-8 gap-1.5 px-3 text-[13px] font-semibold rounded-full [&>svg]:size-3',
				text: 'h-auto p-0 gap-1 text-[13px] [&>svg]:size-[14px]',
				'text-md': 'h-auto p-0 gap-1 text-[15px] [&>svg]:size-4',
				'text-xs': 'h-auto p-0 gap-1 text-[11px] [&>svg]:size-3',
				header: 'h-auto px-1.5 py-0.5 -mx-1.5 -my-0.5 gap-1 text-[12px] rounded-md [&>svg]:size-3',
				// A list heading that opens a menu (topic list bar "Recents v"): the
				// step between a 13px section caption and the panel title. Tight label
				// leading, a 14px trailing chevron; the negative inline margin keeps the
				// text on the gutter while the ghost hover fill bleeds past it. Pair
				// with `ghost` so it reads as text at rest.
				heading: 'h-8 px-2 -mx-2 gap-1 text-[17px] font-semibold leading-[1.2] rounded-[12px] whitespace-nowrap [&>svg]:size-[14px]'
			},
			pressScale: { true: 'active:scale-[0.97]', false: '' },
			iconOnly: {
				true: 'px-0'
			}
		},
		compoundVariants: [
			{ size: 'xs', iconOnly: true, className: 'size-6 [&>svg]:size-3.5' },
			{ size: 'sm', iconOnly: true, className: 'size-8 [&>svg]:size-3.5' },
			{ size: 'md', iconOnly: true, className: 'size-10 [&>svg]:size-[18px]' },
			{ size: 'lg', iconOnly: true, className: 'size-12 [&>svg]:size-6' },
			{ size: 'fab', iconOnly: true, className: 'size-12 [&>svg]:size-5' },
			{ size: 'nav', iconOnly: true, className: 'size-14 rounded-2xl [&>svg]:size-5' },
			{ size: 'nav-sm', iconOnly: true, className: 'size-9 [&>svg]:size-4' },
			// A circle beside `tab-sm` pills (Cmd+K sort control): same height, radius and glyph.
			{ size: 'tab-sm', iconOnly: true, className: 'size-8 [&>svg]:size-3' }
		],
		defaultVariants: {
			variant: 'solid',
			size: 'md',
			pressScale: true
		}
	}
);

export interface ButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	theme?: ButtonTheme;
	/**
	 * In-flight state. The button keeps its resting SIZE and its children stay
	 * mounted (hidden at `opacity-0`) so the label still reserves its width and
	 * the button never twitches; a spinner is absolutely centred over them.
	 *
	 * While loading the button is `disabled` + `aria-busy`, but it keeps
	 * `pointer-events` - a disabled control swallows the click instead of letting
	 * it fall through to a wrapping `<a>`/`<Link>`, which is exactly what a
	 * link-wrapped CTA needs mid-request (otherwise the second click navigates
	 * away while the first is still working).
	 */
	loading?: boolean;
}

// Variants with a transparent resting surface. Inside a `.frosted-ghosts` ancestor
// (styles/frosted-ghosts.css) their hover/focus fill also gets a backdrop blur, so a
// button floating on a photo, film or canvas reads as frosted glass while the
// same button in a menu stays flat. Opt-in per surface, never global.
const FROST_VARIANTS = new Set<NonNullable<ButtonProps['variant']>>([
	'ghost',
	'ghost-muted',
	'ghost-theme',
	'ghost-black',
	'outline',
	'outline-muted',
	'outline-weak',
	'text',
	'header-trigger',
]);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant, size, iconOnly, pressScale, theme, loading, disabled, children, ...props },
		ref
	) => {
		return (
			<button
				className={cn(
					theme && themeClasses[theme],
					buttonVariants({ variant, size, iconOnly, pressScale, className }),
					// The spinner is rendered FIRST, so `&>*+*` is exactly "every real
					// child" - they stay in flow (width preserved) but paint nothing.
					// The disabled dimming/pointer-events defaults are undone here: a
					// loading button should read as itself, and must keep eating clicks.
					loading &&
						'relative cursor-wait disabled:pointer-events-auto disabled:opacity-100 [&>*+*]:opacity-0'
				)}
				data-frost={variant && FROST_VARIANTS.has(variant) ? '' : undefined}
				aria-busy={loading || undefined}
				disabled={disabled || loading}
				ref={ref}
				{...props}
			>
				{loading && (
					<span className="pointer-events-none absolute inset-0 flex items-center justify-center">
						<Spinner className="size-[1.15em]" />
					</span>
				)}
				{/* Bare string/number children are text nodes, which `&>*+*` cannot
				    reach - wrap them so the label hides with everything else. */}
				{loading
					? Children.map(children, (child) =>
							typeof child === 'string' || typeof child === 'number' ? (
								<span>{child}</span>
							) : (
								child
							)
						)
					: children}
			</button>
		);
	}
);
Button.displayName = 'Button';

export { buttonVariants };
